import { CheckIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Judgement, MatchingCandidate } from '@/features/ai-matching-results/lib/types';
import { cn } from '@/shared/lib/utils';
import { Card, Skeleton, Text, Tooltip } from '@/shared/ui';
import { MatchingCandidateRow } from './MatchingCandidateRow';

type MatchingSkillCardViewStatus =
  | 'not-executed'
  | 'loading'
  | 'completed'
  | 'failed-network'
  | 'failed-server';

/**
 * 判定の制御モード（discriminated union）。
 * - 非制御: いずれも未指定。内部 useState で判定を保持する（mock / 財源側の従来挙動）
 * - 制御: judgements + onJudgementChange を渡す。判定値は外部（API 由来）で管理し、変更を委譲する
 */
type JudgementControl =
  | { judgements?: undefined; onJudgementChange?: undefined }
  | {
      judgements: Record<string, Judgement>;
      onJudgementChange: (candidateId: string, next: Judgement) => void;
    };

type BaseProps = {
  /** AI スキルのタイトル（例: 「調査票をもとに特別交付税を探す」） */
  skillTitle: string;
  /** ヘルプアイコンの Tooltip 内容 */
  description?: string;
  /** 候補リスト（completed 状態時に表示） */
  candidates: MatchingCandidate[];
  /** カードの状態 */
  viewStatus: MatchingSkillCardViewStatus;
  /** 現在開いている財源 ID。一致する候補をオレンジでハイライト */
  highlightedFundingSourceId?: string;
  /** カードヘッダー右側のアクション（3 点メニュー等） */
  headerAction?: ReactNode;
  /** not-executed 状態でカード本体中央に表示する実行アクション */
  notExecutedAction?: ReactNode;
  /** failed-server 状態時のエラーメッセージ */
  errorMessage?: string;
  /** failed-network 状態時の再読み込みアクション */
  retryAction?: ReactNode;
};

type Props = BaseProps & JudgementControl;

type Badge = { kind: 'candidates'; count: number } | { kind: 'matched'; count: number };

// バッジは「意味のある件数」がある時だけ出す。
// 未実行・候補0件・実行中等はカード本体（空状態 / スケルトン）が状態を語るためバッジは出さない。
const computeBadge = (
  viewStatus: MatchingSkillCardViewStatus,
  candidates: MatchingCandidate[],
  judgements: Record<string, Judgement>,
): Badge | undefined => {
  if (viewStatus !== 'completed') return undefined;
  const matchedCount = candidates.filter((c) => judgements[c.candidateId] === 'matched').length;
  if (matchedCount > 0) return { kind: 'matched', count: matchedCount };
  if (candidates.length > 0) return { kind: 'candidates', count: candidates.length };
  return undefined;
};

/**
 * マッチングスキルカード（共通コンポーネント）
 *
 * 事業詳細マッチング tab および財源側サイドパネルで共有する AI スキルカード。
 * AI スキルのタイトル + 件数バッジ + 3 点メニューをヘッダーに置き、候補リスト + 判定 Select を本体に表示する。
 * 候補ゼロ時は「候補が見つかりませんでした」、未実行時は「まだ実行していません」+ 実行ボタンを表示する。
 *
 * 判定セレクトの状態は `useState<Record<candidateId, Judgement>>` で局所保持（mock 段階）。
 */
export const MatchingSkillCard = ({
  skillTitle,
  description,
  candidates,
  viewStatus,
  highlightedFundingSourceId,
  headerAction,
  notExecutedAction,
  errorMessage,
  retryAction,
  judgements: controlledJudgements,
  onJudgementChange,
}: Props) => {
  const { t } = useTranslation();

  // 非制御モード用の local state。初期値は candidate.judgement
  const [internalJudgements, setInternalJudgements] = useState<Record<string, Judgement>>(() =>
    Object.fromEntries(candidates.map((c) => [c.candidateId, c.judgement])),
  );

  // 制御モード（onJudgementChange 指定時）は外部値を使い、変更を委譲する
  // 制御モードでは union 制約により controlledJudgements は必ず渡される
  const isControlled = onJudgementChange !== undefined;
  const judgements = controlledJudgements ?? internalJudgements;

  const handleJudgementChange = (candidateId: string, next: Judgement) => {
    if (isControlled) {
      onJudgementChange(candidateId, next);
      return;
    }
    setInternalJudgements((prev) => ({ ...prev, [candidateId]: next }));
  };

  const badge = computeBadge(viewStatus, candidates, judgements);

  return (
    <Card className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1">
          <Text variant="label">{skillTitle}</Text>
          {description && (
            <Tooltip tooltipContent={description}>
              <QuestionMarkCircleIcon className="size-4 text-text-secondary" aria-hidden />
            </Tooltip>
          )}
        </div>
        <div className="flex items-center gap-2">
          {badge && <BadgeLabel badge={badge} />}
          {headerAction}
        </div>
      </div>

      {viewStatus === 'loading' && (
        <div className="flex flex-col gap-2 py-2">
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-4/5" />
          <Skeleton className="h-6 w-full" />
        </div>
      )}

      {viewStatus === 'completed' && candidates.length > 0 && (
        <AccordionPrimitive.Root
          type="multiple"
          className="flex flex-col divide-y divide-border-base"
        >
          {candidates.map((candidate) => (
            <MatchingCandidateRow
              key={candidate.candidateId}
              candidate={candidate}
              judgement={judgements[candidate.candidateId] ?? 'pending'}
              onJudgementChange={(next) => handleJudgementChange(candidate.candidateId, next)}
              highlighted={candidate.fundingSourceId === highlightedFundingSourceId}
            />
          ))}
        </AccordionPrimitive.Root>
      )}

      {viewStatus === 'completed' && candidates.length === 0 && (
        <div className="flex flex-col items-center gap-1 py-4 text-center">
          <Text variant="label">{t('ai-matching-results.matching-skill.no-candidates-title')}</Text>
          <Text variant="description" className="text-text-secondary">
            {t('ai-matching-results.matching-skill.no-candidates-description')}
          </Text>
        </div>
      )}

      {viewStatus === 'not-executed' && (
        <div className="flex flex-col items-center gap-2 py-6 text-center">
          <Text variant="label">{t('ai-matching-results.matching-skill.not-executed-title')}</Text>
          <Text variant="description" className="text-text-secondary">
            {t('ai-matching-results.matching-skill.not-executed-description')}
          </Text>
          {notExecutedAction && <div className="mt-2">{notExecutedAction}</div>}
        </div>
      )}

      {viewStatus === 'failed-network' && (
        <div className="flex items-center justify-between gap-2 py-2">
          <Text variant="description" className="text-text-error">
            {t('ai.errors.analysis-failed-description')}
          </Text>
          {retryAction}
        </div>
      )}

      {viewStatus === 'failed-server' && (
        <Text variant="description" className="text-text-error">
          {errorMessage ?? t('ai.errors.analysis-failed-description')}
        </Text>
      )}
    </Card>
  );
};

const BadgeLabel = ({ badge }: { badge: Badge }) => {
  const { t } = useTranslation();
  if (badge.kind === 'matched') {
    return (
      <span className={cn('flex items-center gap-1 text-text-accent')}>
        <CheckIcon className="size-4" aria-hidden />
        <Text variant="body" className="text-text-accent">
          {t('ai-matching-results.matching-skill.matched-count', { count: badge.count })}
        </Text>
      </span>
    );
  }
  return (
    <Text variant="body" className="text-text-secondary">
      {t('ai-matching-results.project-detail.accordion.candidates-count', { count: badge.count })}
    </Text>
  );
};
